import { Component, computed, effect, inject, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormRecord,
  ReactiveFormsModule,
  FormsModule,
  Validators
} from '@angular/forms';
import { NgbActiveModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { TableModule } from '@openng/optimus-ui/table';
import { CheckboxModule } from '@openng/optimus-ui/checkbox';
import { GenericDropdownComponent, SvgIconsComponent, CurrencyFormatPipe, BtnLoaderDirective } from '@zayan-erp/shared';
import { DropdownData } from '@zayan-erp/shared';
import { toSignal } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { finalize, of } from 'rxjs';
import {
  AddendumProductAttributePayload,
  AddendumProductPayload,
  OptionFilters,
  ProductOption,
  SelectedProductEntry,
  SelectedProductForm
} from '../../../../models/deal-options.models';
import { ModelElevationProductsService } from '../../../../services/model-elevation-products.service';
import { DealOptionFiltersService } from '../../../../services/deal-option-filters.service';
import { SelectedProductPanelComponent } from '../selected-product-panel/selected-product-panel.component';
import { AddendumService } from '../../../../services/addendum.service';

const SALES_OFFICE_CHANNEL = 0;

@Component({
  selector: 'app-add-option-modal',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TableModule,
    CheckboxModule,
    SvgIconsComponent,
    GenericDropdownComponent,
    CurrencyFormatPipe,
    SelectedProductPanelComponent,
    NgbTooltipModule,
    FormsModule,
    BtnLoaderDirective
  ],
  templateUrl: './add-option-modal.component.html',
  styleUrls: ['./add-option-modal.component.scss']
})
export class AddOptionModalComponent {
  private readonly modalRef = inject(NgbActiveModal);
  private readonly fb = inject(FormBuilder);
  private readonly modelElevationProductsService = inject(ModelElevationProductsService);
  private readonly dealOptionFiltersService = inject(DealOptionFiltersService);
  private readonly addendumService = inject(AddendumService);

  readonly modelId = signal<string | null>(null);
  readonly elevationId = signal<string | null>(null);
  readonly addendumId = signal<string | null>(null);
  readonly addedProductIds = signal<string[]>([]);

  readonly searchTerm = signal<string>('');
  readonly filterForm = this.fb.group({
    room: this.fb.control<string | null>(null),
    category: this.fb.control<string | null>(null),
    subcategory: this.fb.control<string | null>(null),
    applyCutoff: this.fb.nonNullable.control(false)
  });

  readonly roomId = toSignal(this.filterForm.controls.room.valueChanges, { initialValue: null });
  readonly categoryId = toSignal(this.filterForm.controls.category.valueChanges, { initialValue: null });
  readonly subcategoryId = toSignal(this.filterForm.controls.subcategory.valueChanges, { initialValue: null });
  readonly applyCutoff = toSignal(this.filterForm.controls.applyCutoff.valueChanges, { initialValue: false });

  readonly filtersResource = rxResourceFixed({
    params: () => ({
      modelId: this.modelId(),
      elevationId: this.elevationId(),
      roomId: this.roomId(),
      categoryId: this.categoryId(),
      subcategoryId: this.subcategoryId()
    }),
    defaultValue: { categories: [], subcategories: [], rooms: [], hasAnyOptions: false } as OptionFilters,
    stream: ({ params }) => {
      if (!params.modelId || !params.elevationId) {
        return of<OptionFilters>({ categories: [], subcategories: [], rooms: [], hasAnyOptions: false });
      }
      return this.dealOptionFiltersService.getOptionFilters(params.modelId, params.elevationId, {
        roomId: params.roomId,
        categoryId: params.categoryId,
        subcategoryId: params.subcategoryId,
        catalogChannel: SALES_OFFICE_CHANNEL
      });
    }
  });

  readonly #initialCategories = signal<DropdownData[] | null>(null);

  readonly categories = computed<DropdownData[]>(
    () =>
      this.#initialCategories() ??
      this.filtersResource.value().categories.map(c => ({ id: c.id, firstLabel: c.description, secondLabel: c.code }))
  );

  readonly subcategories = computed<DropdownData[]>(() =>
    this.filtersResource
      .value()
      .subcategories.map(sc => ({ id: sc.id, firstLabel: sc.description, secondLabel: sc.code }))
  );

  readonly rooms = computed<DropdownData[]>(() =>
    this.filtersResource.value().rooms.map(r => ({ id: r.id, firstLabel: r.name, secondLabel: r.code }))
  );

  readonly hasAnyOptions = computed(() => this.filtersResource.value().hasAnyOptions);
  readonly filtersLoading = computed(() => this.filtersResource.isLoading());

  readonly productsResource = rxResourceFixed({
    params: () => ({
      modelId: this.modelId(),
      elevationId: this.elevationId(),
      roomId: this.roomId(),
      categoryId: this.categoryId(),
      subcategoryId: this.subcategoryId(),
      applyCutoff: this.applyCutoff()
    }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.modelId || !params.elevationId) {
        return of([]);
      }
      return this.modelElevationProductsService.getModelElevationProducts(params.modelId, params.elevationId, {
        roomId: params.roomId,
        categoryId: params.categoryId,
        subcategoryId: params.subcategoryId,
        applyCutoff: params.applyCutoff ?? undefined
      });
    }
  });

  readonly products = computed(
    () =>
      this.productsResource
        .value()
        ?.filter(
          product =>
            product.code.toLowerCase().includes(this.searchTerm().toLowerCase()) ||
            product.description.toLowerCase().includes(this.searchTerm().toLowerCase()) ||
            product.type.toLowerCase().includes(this.searchTerm().toLowerCase()) ||
            product.uom.toLowerCase().includes(this.searchTerm().toLowerCase()) ||
            product.unitPrice.toString().includes(this.searchTerm().toLowerCase())
        ) ?? []
  );

  readonly isLoading = computed(() => this.productsResource.isLoading());
  readonly loadError = computed(() =>
    this.productsResource.error() ? 'Unable to load products for the selected model and elevation.' : null
  );

  readonly selectedEntries = signal<SelectedProductEntry[]>([]);
  readonly selectionsFormArray: FormArray<SelectedProductForm> = this.fb.array<SelectedProductForm>([]);
  readonly selectionValues = toSignal(this.selectionsFormArray.valueChanges, {
    initialValue: this.selectionsFormArray.getRawValue()
  });
  readonly selectedCount = computed(() => this.selectedEntries().length);
  readonly totalSelectedProductsPrice = computed(() => {
    this.selectionValues();
    return this.selectionsFormArray.controls.reduce((total, form) => total + (form.controls.unitPrice.value ?? 0), 0);
  });
  readonly isSaving = signal(false);
  readonly submitAttempted = signal(false);
  #entryIdCounter = 0;

  submitted = output<string[]>();

  constructor() {
    effect(() => {
      this.categoryId();
      this.filterForm.controls.subcategory.setValue(null, { emitEvent: false });
    });

    effect(() => {
      const categories = this.filtersResource.value().categories;
      if (!this.#initialCategories() && categories.length > 0) {
        this.#initialCategories.set(
          categories.map(c => ({ id: c.id, firstLabel: c.description, secondLabel: c.code }))
        );
      }
    });
  }

  close(): void {
    this.modalRef.dismiss();
  }

  setContext(
    modelId: string | null,
    elevationId: string | null,
    addendumId: string | null,
    addedProductIds: (string | null | undefined)[] = []
  ): void {
    this.modelId.set(modelId);
    this.elevationId.set(elevationId);
    this.addendumId.set(addendumId);
    this.addedProductIds.set(addedProductIds.filter((id): id is string => !!id));
  }

  save(): void {
    this.submitAttempted.set(true);

    const addendumId = this.addendumId();
    if (!addendumId) {
      return;
    }

    if (!this.selectedEntries().length) {
      return;
    }

    this.selectionsFormArray.controls.forEach(form => form.markAllAsTouched());
    if (this.selectionsFormArray.invalid) {
      return;
    }

    const payload = this.#buildPayload();
    if (!payload.length) {
      return;
    }

    const addedProductIds = this.selectedEntries()
      .map(entry => entry.product.productId)
      .filter((id): id is string => !!id);

    this.isSaving.set(true);
    this.addendumService
      .addProducts(addendumId, payload)
      .pipe(finalize(() => this.isSaving.set(false)))
      .subscribe({
        next: () => {
          this.submitted.emit(addedProductIds);
          this.modalRef.close();
        },
        error: () => undefined
      });
  }

  addProduct(product: ProductOption): void {
    const form = this.#createSelectionForm(product.unitPrice);
    this.selectionsFormArray.insert(0, form);

    const entry: SelectedProductEntry = {
      id: this.#generateEntryId(),
      product,
      form
    };
    this.selectedEntries.update(entries => [entry, ...entries]);
  }

  removeProduct(entryId: string): void {
    const entries = this.selectedEntries();
    const index = entries.findIndex(entry => entry.id === entryId);
    if (index === -1) {
      return;
    }

    this.selectionsFormArray.removeAt(index);
    this.selectedEntries.update(current => current.filter(entry => entry.id !== entryId));
  }

  getProductValidationErrorCount(form: SelectedProductForm): number {
    let count = 0;

    if (form.errors?.['attributeConfigError']) {
      count += 1;
    }

    if (form.controls.locationId.invalid) {
      count += 1;
    }

    if (form.controls.unitPrice.invalid) {
      count += 1;
    }

    if (form.controls.notes.invalid) {
      count += 1;
    }

    Object.values(form.controls.attributeValues.controls).forEach(control => {
      if (control.invalid) {
        count += 1;
      }
    });

    return count;
  }

  hasProductValidationErrors(form: SelectedProductForm): boolean {
    if (!this.submitAttempted()) {
      return false;
    }
    return this.getProductValidationErrorCount(form) > 0;
  }

  getTotalValidationErrors(): number {
    if (!this.submitAttempted()) {
      return 0;
    }
    return this.selectedEntries().reduce((total, entry) => total + this.getProductValidationErrorCount(entry.form), 0);
  }

  #createSelectionForm(unitPrice: number | null): SelectedProductForm {
    return this.fb.group<{
      locationId: FormControl<string | null>;
      unitPrice: FormControl<number | null>;
      notes: FormControl<string>;
      attributeValues: FormRecord<FormControl<string | null>>;
    }>({
      locationId: this.fb.control<string | null>(null),
      unitPrice: this.fb.control<number | null>(unitPrice, [Validators.required]),
      notes: this.fb.nonNullable.control(''),
      attributeValues: this.fb.record<FormControl<string | null>>({})
    });
  }

  #buildPayload(): AddendumProductPayload[] {
    return this.selectedEntries()
      .map((entry, index) => {
        const form = this.selectionsFormArray.at(index);
        if (!form) {
          return null;
        }

        const locationId = form.controls.locationId.value;
        // if (!locationId) {
        //   return null;
        // }

        const attributesRecord = form.controls.attributeValues.controls;
        const attributes = Object.entries(attributesRecord)
          .map(([attributeId, control]) => {
            const value = control.value;
            if (!value) {
              return null;
            }
            return {
              subCategoryAttributeId: attributeId,
              subCategoryAttributeValueId: value
            } as AddendumProductAttributePayload;
          })
          .filter((attribute): attribute is AddendumProductAttributePayload => !!attribute);

        return {
          modelElevationProductsId: entry.product.modelElevationProductsId,
          selectedLocationId: locationId,
          unitPrice: form.controls.unitPrice.value ?? 0,
          notes: form.controls.notes.value ?? '',
          attributes
        };
      })
      .filter((payload): payload is AddendumProductPayload => !!payload);
  }

  #generateEntryId(): string {
    this.#entryIdCounter += 1;
    return `selection-${Date.now()}-${this.#entryIdCounter}`;
  }

  isProductSelected(product: ProductOption): boolean {
    return (
      this.selectedEntries().some(entry => entry.product.productId === product.productId) ||
      this.addedProductIds().includes(product.productId ?? '')
    );
  }
}
